import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MyHR from "@/pages/MyHR";
import Employees from "@/pages/Employees";
import Approvals from "@/pages/Approvals";
import Recruitment from "@/pages/Recruitment";
import Holidays from "@/pages/Holidays";
import Todo from "@/pages/Todo";
import RolesMatrix from "@/pages/RolesMatrix";
import Analytics from "@/pages/Analytics";
import Payroll from "@/pages/Payroll";
import { Organizations, CompanyOverview, CompanyConfig, UsersAdmin } from "@/pages/Admin";
import { OrgAttendance, AuditLogs } from "@/pages/Logs";
import * as C from "@/pages/Collections";
import Checklists from "@/pages/Checklists";
import Reviews from "@/pages/Reviews";
import BonusOverview from "@/pages/Bonus";
import { AttendancePolicy, LeavePolicy, PerfPolicy, WfhPolicy, BonusPolicy } from "@/pages/Policies";
import TaxConfig from "@/pages/TaxConfig";
import Insurances from "@/pages/Insurance";
import ChatPage from "@/pages/Chat";

function Protected() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center font-serif text-xl text-muted-foreground">Opening the ledger…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell />;
}

function Guard({ perm, children }) {
  const { can } = useAuth();
  if (!can(perm)) return <div className="paper-card p-10 text-center rise" data-testid="no-access"><div className="eyebrow mb-2">403</div><h2 className="font-serif text-2xl">This page isn't in your ledger.</h2><p className="text-sm text-muted-foreground mt-1">Your role doesn't include access to this module.</p></div>;
  return children;
}
const G = (perm, el) => <Guard perm={perm}>{el}</Guard>;

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<Protected />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/my" element={<Navigate to="/my/attendance" replace />} />
            <Route path="/my/:tab" element={<MyHR />} />
            <Route path="/people/employees" element={G("people.employees", <Employees />)} />
            <Route path="/people/recruitment" element={G("people.recruitment", <Recruitment />)} />
            <Route path="/people/onboarding" element={G("people.onboarding", <Checklists kind="onboarding" />)} />
            <Route path="/people/offboarding" element={G("people.offboarding", <Checklists kind="offboarding" />)} />
            <Route path="/people/resigned" element={G("people.resigned", <Employees resigned />)} />
            <Route path="/people/contacts" element={G("people.contacts", <C.Contacts />)} />
            <Route path="/time/attendance" element={G("time.attendance", <OrgAttendance />)} />
            <Route path="/time/devices" element={G("time.device_attendance", <C.Devices />)} />
            <Route path="/time/leave" element={G("time.leave", <Approvals fixedType="leave" eyebrow="Time & Attendance" title="Leave" />)} />
            <Route path="/time/overtime" element={G("time.overtime", <Approvals fixedType="overtime" eyebrow="Time & Attendance" title="Overtime" />)} />
            <Route path="/time/holidays" element={G("time.holidays", <Holidays />)} />
            <Route path="/time/corrections" element={G("time.corrections", <Approvals fixedType="attendance_correction" eyebrow="Time & Attendance" title="Attendance corrections" />)} />
            <Route path="/requests" element={<Approvals />} />
            <Route path="/requests/:type" element={<Approvals />} />
            <Route path="/performance/reviews" element={G("performance.reviews", <Reviews />)} />
            <Route path="/performance/audits" element={G("performance.audits", <Reviews audit />)} />
            <Route path="/payroll/payslips" element={G("payroll.payslips", <Payroll />)} />
            <Route path="/payroll/tax" element={G("payroll.tax", <TaxConfig />)} />
            <Route path="/payroll/analytics" element={G("payroll.analytics", <Analytics finance />)} />
            <Route path="/payroll/salary_advance" element={G("payroll.salary_advance", <Approvals fixedType="salary_advance" eyebrow="Payroll & Finance" title="Salary advances" />)} />
            <Route path="/payroll/reimbursement" element={G("payroll.reimbursement", <Approvals fixedType="reimbursement" eyebrow="Payroll & Finance" title="Reimbursements" />)} />
            <Route path="/payroll/expense" element={G("payroll.expense", <C.Expenses />)} />
            <Route path="/payroll/bonus" element={G("payroll.bonus", <BonusOverview />)} />
            <Route path="/people/insurances" element={G("people.insurances", <Insurances />)} />
            <Route path="/time/wfh" element={G("time.wfh", <Approvals fixedType="wfh" eyebrow="Time & Attendance" title="Work from home" />)} />
            <Route path="/policies/attendance" element={G("policy.attendance", <AttendancePolicy />)} />
            <Route path="/policies/leave" element={G("policy.leave", <LeavePolicy />)} />
            <Route path="/policies/performance" element={G("policy.performance", <PerfPolicy />)} />
            <Route path="/policies/wfh" element={G("policy.wfh", <WfhPolicy />)} />
            <Route path="/policies/bonus" element={G("policy.bonus", <BonusPolicy />)} />
            <Route path="/reports/analytics" element={G("reports.analytics", <Analytics />)} />
            <Route path="/reports/custom" element={G("reports.custom", <C.CustomReports />)} />
            <Route path="/collab/contacts" element={G("collab.contacts", <C.Contacts />)} />
            <Route path="/collab/meetings" element={G("collab.meetings", <C.Meetings />)} />
            <Route path="/collab/discuss" element={G("collab.discuss", <ChatPage />)} />
            <Route path="/workspace/announcements" element={G("workspace.announcements", <C.Announcements />)} />
            <Route path="/workspace/overview" element={G("workspace.overview", <CompanyOverview />)} />
            <Route path="/workspace/todo" element={G("workspace.todo", <Todo />)} />
            <Route path="/workspace/inventory" element={G("workspace.inventory", <C.Inventory />)} />
            <Route path="/admin/organizations" element={G("admin.organizations", <Organizations />)} />
            <Route path="/admin/users" element={G("admin.users", <UsersAdmin />)} />
            <Route path="/admin/roles" element={G("admin.roles", <RolesMatrix />)} />
            <Route path="/admin/settings" element={G("admin.settings", <C.Settings />)} />
            <Route path="/admin/audit" element={G("admin.audit", <AuditLogs />)} />
            <Route path="/admin/config" element={G("admin.company_config", <CompanyConfig />)} />
            <Route path="/admin/integrations" element={G("admin.integrations", <C.Integrations />)} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}
